import { useState, useCallback } from 'react'
import moment from 'moment-timezone'
import { keys } from 'ramda'

import useCallbackRef from 'app/common/hooks/useCallbackRef'

import DaysGrid from './days-grid'
import WeekdaysGrid from './weekdays-grid'
import MonthSwitcher from './month-switcher'
import type { DaysState } from './use-days-slected-state'
import { dateFormat as dateFormatDefault } from './date-format'

import styles from './plain-calendar.module.scss'

/**
 *  Компонент календаря
 */

export type PlainCalendarProps = {
  className?: string
  daysSelected?: DaysState
  dateFormat?: string
  markedDatesFrom?: string
  markedDatesTo?: string
  getIsDayMarked?: (dayFarmatted: string, format: string) => boolean
  getMarkColor?: (dayFarmatted: string, format: string) => string
  calendarCursor?: () => string
  hideYear?: boolean
  weekSelection?: boolean
  onDayClick: (date: string, format: string) => void
  onMonthChange?: (date: string, format: string) => void
  onYearChange?: (date: string, format: string) => void
  dateDisabledStatus?: (dayFormatted: string, format: string, unit?: string) => boolean
  disableMonthSwitcherByStatus?: boolean
}

const PlainCalendar = ({
  className,
  daysSelected = {},
  dateFormat = dateFormatDefault,
  markedDatesFrom,
  markedDatesTo,
  getIsDayMarked,
  getMarkColor,
  calendarCursor,
  hideYear,
  weekSelection,
  onDayClick,
  onMonthChange,
  onYearChange,
  dateDisabledStatus,
  disableMonthSwitcherByStatus,
}: PlainCalendarProps) => {
  const [monthDate, setMonthDate] = useState(
    calendarCursor?.() || keys(daysSelected)[0] || moment().format(dateFormat),
  )

  const handlePrevMonth = () => {
    const prevMonth = moment(monthDate, dateFormat).subtract(1, 'month').format(dateFormat)
    setMonthDate(prevMonth)
    onMonthChange?.(prevMonth, dateFormat)
  }

  const handleNextMonth = () => {
    const nextMonth = moment(monthDate, dateFormat).add(1, 'month').format(dateFormat)
    setMonthDate(nextMonth)
    onMonthChange?.(nextMonth, dateFormat)
  }

  const handleMonth = (nm: number) => {
    const newMonth = moment(monthDate, dateFormat).month(nm).format(dateFormat)
    setMonthDate(newMonth)
    onMonthChange?.(newMonth, dateFormat)
  }

  const handleYear = (ny: number) => {
    const newYear = moment(monthDate, dateFormat).year(ny).format(dateFormat)
    setMonthDate(newYear)
    onYearChange?.(newYear, dateFormat)
  }

  return (
    <div className={className}>
      <MonthSwitcher
        monthDate={monthDate}
        dateFormat={dateFormat}
        onPrev={handlePrevMonth}
        onNext={handleNextMonth}
        onMonth={handleMonth}
        onYear={handleYear}
        dateDisabledStatus={dateDisabledStatus}
        disableMonthSwitcherByStatus={disableMonthSwitcherByStatus}
        hideYear={hideYear}
      />
      <WeekdaysGrid className={styles.weekdays} />
      <DaysGrid
        className={styles.days}
        daysSelected={daysSelected}
        onClickDay={onDayClick}
        monthDate={monthDate}
        dateFormat={dateFormat}
        dateDisabledStatus={dateDisabledStatus}
        markedDatesFrom={markedDatesFrom}
        markedDatesTo={markedDatesTo}
        getIsDayMarked={getIsDayMarked}
        getMarkColor={getMarkColor}
        weekSelection={weekSelection}
      />
    </div>
  )
}

export default PlainCalendar
